import React, { useState } from 'react';
import { Trans } from '@lingui/macro';
import Tutorial from './Tutorial';
import City from '../City';
import PlayerProfiles from '../PlayerProfiles';
import PlayerCards from '../PlayerCards';
import DiceRoll from '../DiceRoll';
import EnergyContainer from '../EnergyContainer';
import TableCards from '../TableCards';
import { useDispatch } from 'react-redux';
import { endTutorialDialog } from '../../../redux/tutorialSlice';

const tutorialStages = ['player-profiles', 'city', 'dice', 'energy', 'table', 'player-cards'];
const tutorialSubstages = {
  'player-profiles': [{
    title: <Trans>Players</Trans>,
    desc: <Trans>This is the summary of the players playing this match, and where you can see all of their stats.<br /><br />You can also see the cards a player has in their hand by clicking on their monster icon.</Trans>
  },
  {
    title: <Trans>Players: Health</Trans>,
    desc: <Trans>Keep an eye at your health regularly. Health will go down when you are damaged, and can be recovered from dice rolls or cards.<br /><br />If your health hits 0, you will lose.</Trans>
  },
  {
    title: <Trans>Players: Stars</Trans>,
    desc: <Trans>The stars are also important: if you reach 20 stars, you win! You can get stars by rolling dice, being in town, or from cards.</Trans>
  }],
  'city': [{
    title: <Trans>Town</Trans>,
    desc: <Trans>The town is the center stage of the game. You are competing against all the other players for control over the town!
    Entering the town will grant you 1 star, and starting your turn inside of it will grant you 2 stars.<br /><br />
    But beware! While inside the town you will not be able to use the dice to heal, and you will be fighting all the players outside the town at the same time.
  </Trans>
  }],
  'dice': [{
    title: <Trans>Dice</Trans>,
    desc: <Trans>This is the area where the dice are rolled. The dice can heal you, grant you stars, or damage other players.</Trans>
  }, {
    title: <Trans>Dice: Re-rolls</Trans>,
    desc: <Trans>Once you've rolled your dice the first time, you can click on the dice that you would like to re-roll for up to 2 times!<br /><br />If you don't wish to re-roll any dice, you can click the checkmark to accept your roll</Trans>
  }, {
    title: <Trans>Dice: Stars</Trans>,
    desc: <Trans>If you roll three dice with the same number you will get as many stars as the number on the die says. If you roll more dice with that same number, you will receive an extra star for each of the dice.<br /><br />For example, if you roll...
      <ul>
        <li><span className="game-sprite tiny-icon roll-2"></span><span className="game-sprite tiny-icon roll-2"></span><span className="game-sprite tiny-icon roll-2"></span><span className="game-sprite tiny-icon roll-2"></span>> Receive 2 + 1 = 3 stars</li>
        <li><span className="game-sprite tiny-icon roll-1"></span><span className="game-sprite tiny-icon roll-1"></span><span className="game-sprite tiny-icon roll-1"></span>> Receive 1 star</li>
        <li><span className="game-sprite tiny-icon roll-3"></span><span className="game-sprite tiny-icon roll-3"></span><span className="game-sprite tiny-icon roll-3"></span><span className="game-sprite tiny-icon roll-2"></span><span className="game-sprite tiny-icon roll-1"></span>> Receive 3 stars</li>
      </ul>
    </Trans>
  }],
  'energy': [{
    title: <Trans>Energy</Trans>,
    desc: <Trans>This is the amount of energy you have stored at the moment. You can use it to buy cards!</Trans>
  }],
  'table': [{
    title: <Trans>Table</Trans>,
    desc: <Trans>These are the cards currently on the table, which you can buy with energy.<br /><br />There are two types of cards: use-once and passive. <ul><li>Use-once cards need to be clicked in order to play them</li><li>Passive cards give you passive effects which might work even outside your turn</li></ul></Trans>
  },
  {
    title: <Trans>Table: Swapping cards</Trans>,
    desc: <Trans>By clicking on the swap icon (and for a small energy cost), you can discard the three cards that are on the table and bring in new ones from the deck.<br /><br />Always keep in mind that the deck isn't infinite!</Trans>
  }],
  'player-cards': [{
    title: <Trans>Your cards</Trans>,
    desc: <Trans>This is the area where your cards will live.<br /><br />You can play a use-once (red) card by clicking on it. Passive (blue) cards can't be played, their effects will trigger when the conditions in the card are fulfilled.</Trans>
  }],
};

export default function GameAreaWithTutorial(props) {
  const dispatch = useDispatch();
  const [tutorialStageArea, setTutorialStage] = useState(0);
  const [tutorialSubstage, setTutorialSubstage] = useState(0);
  const curStageId = tutorialStages[tutorialStageArea];

  const advanceStage = () => {
    if (tutorialStageArea + 1 >= tutorialStages.length) {
      dispatch(endTutorialDialog());
    } else {
      setTutorialSubstage(0);
      setTutorialStage(tutorialStageArea + 1);
    }
  };
  const advanceTutorial = () => {
    if (tutorialSubstage + 1 >= tutorialSubstages[curStageId].length) {
      advanceStage();
    } else {
      setTutorialSubstage(tutorialSubstage + 1);
    }
  };

  const withTutorial = (id, location, component) => {
    if (curStageId !== id) {
      return component;
    }

    return <Tutorial
      stageName={id}
      alignment={location}
      next={advanceTutorial}
      title={tutorialSubstages[id][tutorialSubstage].title}
      desc={tutorialSubstages[id][tutorialSubstage].desc}
    >
      {component}
    </Tutorial>
  };

  return <>
    {withTutorial('city', 'right', <City />)}
    {withTutorial('player-profiles', 'right', <PlayerProfiles />)}
    {withTutorial('player-cards', 'top', <PlayerCards />)}

    <div id="player-action-panel-container">
      <div className="center-structure">
        {withTutorial('dice', 'left', <DiceRoll />)}
        {withTutorial('energy', 'left', <EnergyContainer />)}
      </div>

      {withTutorial('table', 'left', <TableCards />)}
    </div>
  </>;
}
